import React, { useContext, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { ACCOUNTS_BLOCK_BOOKING_NO_POSITION } from '@ubeya/shared/utils/accounts';
import { RULE_TYPE_MAP_BY_SLUG } from '@ubeya/shared-web/constants';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import useLeaveTypes from '@ubeya/shared/hooks/account/useLeaveTypes';
import usePtoTypes from '@ubeya/shared/hooks/generic/usePtoTypes';
import { showAllBranchesMode } from 'shared/atoms/scheduling';
import useBookingPrompt from './useBookingPrompt';
import { BOOKING_USER_PROMPT_CASES, SLOT_WARNING_TYPES } from 'scheduling/constants';
import { checkShouldRenderWarningBorderAvailableEmployee } from 'scheduling/utils';
import { formatLeaveWarningLabel, getLeaveSlotWarnings } from 'scheduling/leaveIndications';

export const useAvailabilityEmployeeWarnings = ({
  user,
  positionId,
  warnings = [],
  rejected = false,
  unbooked = false
}) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { mappedPositions } = usePositions();
  const { checkPromptCase } = useBookingPrompt({ shift: { positionId } });
  const showAllBranches = useRecoilValue(showAllBranchesMode);
  const {
    account: { config: { blockOverlappingShiftsAcrossBranches = false } = {} }
  } = useAccountConfig();
  const { leaveTypes } = useLeaveTypes();
  const { mappedPtoTypes } = usePtoTypes();

  const mappedLeaveTypes = useMemo(
    () => (leaveTypes || []).reduce((all, leaveType) => ({ ...all, [leaveType.id]: leaveType }), {}),
    [leaveTypes]
  );

  return useMemo(() => {
    const explanationMarkW = [];
    const userCardW = [...(warnings || [])];
    let isBlocking = false;

    if (!showAllBranches && user?.warnings?.length) {
      isBlocking = user.warnings.some(
        ({ type, isBlocking: warningIsBlocking = false }) =>
          ![SLOT_WARNING_TYPES.COMPLIANCE.type, SLOT_WARNING_TYPES.WTD.type].includes(type) && warningIsBlocking
      );
    }

    const bookingUserPromptCase = checkPromptCase(user);

    switch (bookingUserPromptCase.type) {
      case BOOKING_USER_PROMPT_CASES.COMPLIANCE_BLOCK: {
        isBlocking = true;
        explanationMarkW.push({
          type: SLOT_WARNING_TYPES.COMPLIANCE.type,
          format: (
            <div>
              <Trans
                i18nKey="employeeNotCompliantAndBlockedToPosition"
                values={{
                  name: `${user?.firstName} ${user?.lastName}`,
                  position: mappedPositions?.[positionId]?.label
                }}
                components={{ bold: <strong /> }}
              />
            </div>
          )
        });
        break;
      }

      case BOOKING_USER_PROMPT_CASES.EXCEED_WORKING_HOURS: {
        const ruleData = bookingUserPromptCase?.metaData?.[0]?.rules?.[0];
        const isRuleBlocking = ruleData?.type === RULE_TYPE_MAP_BY_SLUG.BLOCK;

        if (isRuleBlocking) {
          isBlocking = true;
        }

        explanationMarkW.push({
          type: SLOT_WARNING_TYPES.WTD.type,
          format: (
            <div>
              {isRuleBlocking && t ? `${t('blockingRule')}: ` : ''}
              <Trans
                i18nKey={
                  bookingUserPromptCase?.isCurrentIssue
                    ? 'employeeExceedWorkingHoursBody'
                    : 'employeeExceedWorkingHoursFutureBody'
                }
                values={{
                  period: ruleData?.period,
                  hours: ruleData?.hours,
                  name: bookingUserPromptCase?.metaData?.[0]?.name,
                  employeeName: [user?.firstName, user?.lastName]?.filter((val) => !!val)?.join(' ')
                }}
                components={{ bold: <strong /> }}
              />
            </div>
          )
        });
        break;
      }

      case BOOKING_USER_PROMPT_CASES.COMPLIANCE:
        explanationMarkW.push({
          type: SLOT_WARNING_TYPES.COMPLIANCE.type,
          format: (
            <div>
              <Trans
                i18nKey="employeeNotCompliantToPosition"
                values={{
                  name: `${user?.firstName} ${user?.lastName}`,
                  position: mappedPositions?.[positionId]?.label
                }}
                components={{ bold: <strong /> }}
              />
            </div>
          )
        });
        break;

      case BOOKING_USER_PROMPT_CASES.POSITION:
        userCardW.push('noPositionBulkBooking');

        if (ACCOUNTS_BLOCK_BOOKING_NO_POSITION.includes(accountId)) {
          explanationMarkW.push({
            type: SLOT_WARNING_TYPES.NO_POSITION.type,
            format: (
              <div>
                <Trans i18nKey="noPositionBulkBooking" />
              </div>
            )
          });
          isBlocking = true;
        }
        break;

      default:
    }

    // leave warnings are surfaced outside the prompt-case switch so a higher priority case
    // (position, compliance...) never hides the fact that the employee is on leave that day
    getLeaveSlotWarnings(user?.warnings).forEach((warning) => {
      explanationMarkW.push({
        type: warning.type,
        format: formatLeaveWarningLabel({ warning, mappedLeaveTypes, mappedPtoTypes, t })
      });
    });

    const shouldRenderWarningBorder = checkShouldRenderWarningBorderAvailableEmployee({
      rejected,
      unbooked,
      warnings: user?.warnings ?? []
    });

    if (blockOverlappingShiftsAcrossBranches) {
      const hasOverlappingShift = (user.warnings || []).some(
        (warning) => warning.type === SLOT_WARNING_TYPES.OVERLAPPING_SHIFTS.type
      );
      if (hasOverlappingShift) {
        isBlocking = true;
      }
    }

    return {
      explanationMarkWarnings: explanationMarkW,
      userCardWarnings: userCardW,
      existsBlockingWarning: isBlocking,
      shouldRenderWarningBorder
    };
  }, [
    warnings,
    showAllBranches,
    user,
    checkPromptCase,
    rejected,
    unbooked,
    blockOverlappingShiftsAcrossBranches,
    mappedLeaveTypes,
    mappedPtoTypes,
    mappedPositions,
    positionId,
    accountId,
    t
  ]);
};
